import React, { useMemo } from 'react';
import { AggregateHeaderCell } from '../components/Gradebook/AggregateHeaderCell';
import { StudentCell } from '../components/Gradebook/StudentCell';
import { GradeCell } from '../components/Gradebook/GradeCell';
import { EditableGradeCell } from '../components/Gradebook/EditableGradeCell';
import { AssignmentHeaderCell } from '../components/Gradebook/AssignmentHeaderCell';
import { formatDate } from '../components/Gradebook/utils';
import { COLUMN_WIDTHS } from '../components/Gradebook/constants';
import styles from '../components/Gradebook/Gradebook.module.css';
import {
  getToolIds,
  shouldHideSidePanel,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';
import { getConfigObject } from '../../../../utils/config';
import _isEmpty from 'lodash/isEmpty';

/**
 * Hook to build TanStack Table column definitions for the gradebook
 *
 * Creates column configurations for:
 * - Student name column (sticky)
 * - Student ID column (conditional)
 * - Course total column
 * - Assignment columns (grouped by category)
 * - Category total columns
 *
 * @param {Object} options - Column configuration options
 * @param {Object} options.data - Gradebook data with categories and assignments
 * @param {Map} options.categoryGroups - Assignments grouped by category ID
 * @param {Set} options.collapsedCategories - Set of collapsed category IDs
 * @param {Function} options.getColumnSortState - Function to get sort state for a column
 * @param {Function} options.handleSort - Sort handler function
 * @param {boolean} options.showPercent - Whether to show percentages or points
 * @param {Function} options.handleGradeClick - Grade cell click handler
 * @param {Function} options.handleStudentClick - Student name click handler
 * @param {Function} options.handleAssignmentHeaderClick - Assignment header click handler
 * @param {Function} options.showGradeOverrideDialog - Grade override dialog function
 * @param {number} options.roundPercentType - Rounding precision setting
 * @param {Array} options.rdxGradebookSettings - Redux gradebook settings
 * @param {boolean} options.requireStudentId - Whether to show student ID column
 * @param {boolean} options.disableManualAssignmentsMenu - Whether to disable manual assignment actions
 * @param {number} options.gradingType - Grading type (points or weighted)
 * @returns {Array} TanStack Table column definitions
 */
export function useGradebookColumns({
  data,
  categoryGroups,
  collapsedCategories,
  getColumnSortState,
  handleSort,
  showPercent,
  handleGradeClick,
  handleStudentClick,
  handleAssignmentHeaderClick,
  showGradeOverrideDialog,
  roundPercentType,
  rdxGradebookSettings,
  requireStudentId,
  disableManualAssignmentsMenu,
  gradingType,
}) {
  return useMemo(() => {
    const cols = [];
    const hasStudents = data.hasStudents;

    // Student name column (only when there are students)
    if (hasStudents) {
      cols.push({
        id: 'name',
        header: () => (
          <AggregateHeaderCell
            columnId="name"
            label={<b>Name</b>}
            sortState={getColumnSortState('name')}
            onSort={handleSort}
          />
        ),
        accessorFn: (row) => row.student.name,
        cell: ({ row }) => (
          <StudentCell
            firstName={row.original.student.firstName}
            lastName={row.original.student.lastName}
            email={row.original.student.email}
            studentId={row.original.student.id}
            onStudentClick={handleStudentClick}
            isBrokenSyncReefGrades={row.original.student.isBrokenSyncReefGrades}
          />
        ),
        size: COLUMN_WIDTHS.NAME,
        meta: { columnIndex: cols.length },
      });

      // Student ID column (conditional based on requireStudentId setting)
      if (requireStudentId) {
        cols.push({
          id: 'studentId',
          header: () => <span className={styles.srOnly}>Student ID</span>,
          accessorFn: (row) => row.student.thirdPartyUserId,
          cell: ({ row }) => {
            const studentId = row.original.student.thirdPartyUserId;
            return (
              <div className={styles.studentIdCell}>
                {studentId || 'Not entered'}
              </div>
            );
          },
          size: COLUMN_WIDTHS.STUDENT_ID,
          meta: { isStudentIdColumn: true, columnIndex: cols.length },
        });
      }

      // Course total column (only when there are students)
      cols.push({
        id: 'courseTotal',
        header: () => (
          <AggregateHeaderCell
            columnId="courseTotal"
            label="Course Total"
            sortState={getColumnSortState('courseTotal')}
            onSort={handleSort}
          />
        ),
        accessorFn: (row) => row.courseTotal,
        cell: ({ row }) => (
          <GradeCell
            grade={{
              studentGrade: row.original.courseTotal / 100, // Convert back to 0-1 decimal
              studentPoints: row.original.coursePointsEarned,
              assigned_points: row.original.coursePointsPossible,
              isAssigned: true,
            }}
            showPercent={showPercent}
            roundPercentType={roundPercentType}
            settings={rdxGradebookSettings}
            showOutOf={!showPercent}
          />
        ),
        size: COLUMN_WIDTHS.COURSE_TOTAL,
        meta: { isCourseTotalColumn: true, columnIndex: cols.length },
      });
    }

    // Add columns for each category
    data.categories.forEach((category) => {
      if (!collapsedCategories.has(category.id)) {
        const assignments = categoryGroups.get(category.id) || [];

        // Add assignment columns
        assignments.forEach((assignment) => {
          cols.push(
            buildAssignmentColumn({
              assignment,
              category,
              cols,
              getColumnSortState,
              handleSort,
              handleAssignmentHeaderClick,
              disableManualAssignmentsMenu,
              showGradeOverrideDialog,
              handleGradeClick,
              showPercent,
              roundPercentType,
              rdxGradebookSettings,
            }),
          );
        });

        // Add category total column (expanded)
        cols.push(
          buildCategoryTotalColumn({
            category,
            cols,
            getColumnSortState,
            handleSort,
            showPercent,
            roundPercentType,
            rdxGradebookSettings,
            isCollapsed: false,
            gradingType,
          }),
        );
      } else {
        // Collapsed category - show only the total with category name
        cols.push(
          buildCategoryTotalColumn({
            category,
            cols,
            getColumnSortState,
            handleSort,
            showPercent,
            roundPercentType,
            rdxGradebookSettings,
            isCollapsed: true,
            gradingType,
          }),
        );
      }
    });

    return cols;
  }, [
    data.categories,
    data.hasStudents,
    categoryGroups,
    collapsedCategories,
    getColumnSortState,
    handleSort,
    showPercent,
    handleGradeClick,
    showGradeOverrideDialog,
    handleStudentClick,
    handleAssignmentHeaderClick,
    roundPercentType,
    rdxGradebookSettings,
    requireStudentId,
    disableManualAssignmentsMenu,
    gradingType,
  ]);
}

/**
 * Determine if an assignment header has a clickable link that opens the side panel.
 * Matches the logic in AssignmentHeaderCell.jsx for consistency.
 *
 * An assignment is clickable when:
 * - The side panel feature flag is enabled
 * - The tool type supports the side panel (not reef/iClicker/manual/empty)
 * - A click handler is provided
 *
 * @param {Object} assignment - The assignment object
 * @param {boolean} hasClickHandler - Whether a click handler is provided
 * @returns {boolean} True if the assignment header should be clickable
 */
function isAssignmentClickable(assignment, hasClickHandler) {
  const { FF_SIDE_PANEL_ACTIVE = false } = getConfigObject();
  const shouldSidePanelBeActive =
    FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(assignment?.tool);

  // Non-linkable tools - reef/iClicker are not clickable
  const { reef, manual } = getToolIds();
  const isManualAssignment = assignment?.tool === manual;
  const isNonLinkableTool = { [reef]: true, [manual]: true };
  const isToolNonLinkable =
    !isManualAssignment &&
    (_isEmpty(assignment?.tool) || isNonLinkableTool[assignment?.tool]);

  return shouldSidePanelBeActive && hasClickHandler && !isToolNonLinkable;
}

/**
 * Build a column definition for an assignment
 */
function buildAssignmentColumn({
  assignment,
  category,
  cols,
  getColumnSortState,
  handleSort,
  handleAssignmentHeaderClick,
  disableManualAssignmentsMenu,
  showGradeOverrideDialog,
  handleGradeClick,
  showPercent,
  roundPercentType,
  rdxGradebookSettings,
}) {
  // Check if this assignment has a clickable link
  const hasClickableLink = isAssignmentClickable(
    assignment,
    !!handleAssignmentHeaderClick,
  );

  return {
    id: assignment.id,
    header: () => (
      <AssignmentHeaderCell
        name={assignment.name}
        dueDate={assignment.dueDate}
        formatDate={formatDate}
        columnId={assignment.id}
        sortState={getColumnSortState(assignment.id)}
        onSort={handleSort}
        assignment={assignment}
        disableManualAssignmentActions={disableManualAssignmentsMenu}
        onAssignmentClick={handleAssignmentHeaderClick}
      />
    ),
    accessorFn: (row) => row.grades[assignment.id]?.studentGrade,
    cell: ({ row, column }) => {
      const grade = row.original.grades[assignment.id];
      const student = row.original.student;
      // Match V1 editability conditions:
      // 1. showGradeOverrideDialog must exist
      // 2. grade must be assigned (isAssigned)
      // Note: V1 does NOT check for isUngraded - ungraded cells ARE clickable
      // and can open the side panel. The handleGradeClick callback handles
      // the side panel vs dialog logic appropriately.
      const isEditable = !!showGradeOverrideDialog && grade?.isAssigned;
      // Get column index from meta for data-grid-id
      const colIndex = column.columnDef.meta?.columnIndex;
      return (
        <EditableGradeCell
          grade={grade}
          showPercent={showPercent}
          roundPercentType={roundPercentType}
          settings={rdxGradebookSettings}
          studentId={student.id}
          studentName={student.name}
          assignmentId={assignment.id}
          assignmentName={assignment.name}
          onGradeClick={handleGradeClick}
          isEditable={isEditable}
          rowIndex={row.index + 3}
          columnIndex={colIndex}
        />
      );
    },
    size: COLUMN_WIDTHS.ASSIGNMENT,
    meta: {
      category: category.id,
      assignmentId: assignment.id,
      assignmentName: assignment.name,
      assignment, // Full assignment object for keyboard activation
      hasClickableLink, // Whether the assignment header has a clickable link
      columnIndex: cols.length, // Store current column index for data-grid-id
    },
  };
}

/**
 * Build a column definition for a category total
 */
function buildCategoryTotalColumn({
  category,
  cols,
  getColumnSortState,
  handleSort,
  showPercent,
  roundPercentType,
  rdxGradebookSettings,
  isCollapsed,
  gradingType,
}) {
  const label = isCollapsed ? `${category.name} Total` : 'Category Total';
  const isWeightedGradingType =
    gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted;

  return {
    id: `category-total-${category.id}`,
    header: () => (
      <AggregateHeaderCell
        columnId={`category-total-${category.id}`}
        label={label}
        sortState={getColumnSortState(`category-total-${category.id}`)}
        onSort={handleSort}
      />
    ),
    accessorFn: (row) => row.categoryTotals[category.id],
    cell: ({ row }) => {
      const catTotal = row.original.categoryTotals[category.id];

      // Handle blank category totals (placeholder rows) - show empty (matches V1)
      if (typeof catTotal === 'object' && catTotal?.blank) {
        return null;
      }

      // Handle ungraded categories - show "X completed" instead of percentage
      if (
        category.isUngraded ||
        (typeof catTotal === 'object' && catTotal?.isUngraded)
      ) {
        const numCompleted =
          typeof catTotal === 'object' ? catTotal.numCompleted || 0 : 0;
        return (
          <div className={styles.ungradedTotal}>
            <span className={styles.ungradedNumber}>{numCompleted} </span>
            <span className={styles.ungradedText}>completed</span>
          </div>
        );
      }

      // Handle graded categories - use percentage and points data
      let gradeValue;
      if (isWeightedGradingType && showPercent) {
        // Weighted mode: use weightedCategoryTotal (already a percentage 0-100)
        gradeValue =
          typeof catTotal === 'object'
            ? (catTotal.weightedCategoryTotal ?? 0) / 100
            : 0;
      } else {
        // Standard mode: use studentGrade (decimal 0-1)
        gradeValue =
          typeof catTotal === 'object'
            ? (catTotal.studentGrade ?? catTotal.percentage / 100)
            : 0;
      }

      return (
        <GradeCell
          grade={{
            studentGrade: gradeValue,
            studentPoints:
              typeof catTotal === 'object' ? catTotal.studentPoints : 0,
            assigned_points:
              typeof catTotal === 'object' ? catTotal.assigned_points : 0,
            isAssigned: true,
          }}
          showPercent={showPercent}
          roundPercentType={roundPercentType}
          settings={rdxGradebookSettings}
        />
      );
    },
    size: COLUMN_WIDTHS.CATEGORY_TOTAL,
    meta: {
      category: category.id,
      isCategoryTotal: true,
      isUngraded: category.isUngraded,
      columnIndex: cols.length,
    },
  };
}

export default useGradebookColumns;
